import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CardComp } from "../../../components";
import { BsArrowRight } from "react-icons/bs";
import UpdateSubscriptionModal from "../../../components/Modules/UpdateSubscriptionModal";
import {
  useGetAllCooperativesQuery,
  useGetDashboardQuery,
  useGetActiveCooperativesSubscriptionsQuery,
  useGetExpiringCooperativesSubscriptionsQuery,
  useGetExpiredCooperativesSubscriptionsQuery,
} from "../../../store/auth/authApi";
import { formatMoney } from "../../../utils";
import EmptyTable from "../../../components/Common/EmptyTable.jsx";

function CooperativesListSkeleton({ rows = 5 }) {
  return (
    <div className="space-y-0" aria-busy="true" aria-label="Loading cooperatives">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="border-0 shadow p-4 rounded-xl bg-white mb-4 animate-pulse"
        >
          <div className="flex flex-col gap-6 lg:flex-row lg:justify-between lg:items-center">
            <div className="flex items-center flex-grow min-w-0">
              <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0" />
              <div className="ml-3 space-y-2 flex-1 min-w-0">
                <div className="h-5 bg-gray-200 rounded w-[min(100%,14rem)]" />
                <div className="h-4 bg-gray-100 rounded w-[min(100%,20rem)]" />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6 lg:gap-8 lg:justify-end">
              {[0, 1, 2, 3].map((j) => (
                <div key={j} className="space-y-2 min-w-[4.5rem]">
                  <div className="h-7 bg-gray-200 rounded w-12" />
                  <div className="h-3.5 bg-gray-100 rounded w-16" />
                </div>
              ))}
              <div className="flex items-center gap-3 shrink-0">
                <div className="h-9 w-24 bg-gray-200 rounded-md" />
                <div className="w-5 h-5 bg-gray-100 rounded" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CooperativesList() {
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedCooperative, setSelectedCooperative] = useState(null);
  const [isUpdatingSubscription, setIsUpdatingSubscription] = useState(false);
  const navigate = useNavigate();

  // Dashboard data for overview metrics
  const { data: dashboardData } = useGetDashboardQuery();

  // Default query for all cooperatives
  const {
    data: allCooperativesData,
    isLoading: allCooperativesLoading,
    error: allCooperativesError,
  } = useGetAllCooperativesQuery();

  // Conditional queries based on filter
  const {
    data: activeSubscriptionsData,
    isLoading: activeSubscriptionsLoading,
    error: activeSubscriptionsError,
  } = useGetActiveCooperativesSubscriptionsQuery(undefined, {
    skip: filterStatus !== "active",
  });

  const {
    data: expiringSubscriptionsData,
    isLoading: expiringSubscriptionsLoading,
    error: expiringSubscriptionsError,
  } = useGetExpiringCooperativesSubscriptionsQuery(undefined, {
    skip: filterStatus !== "expiring",
  });

  const {
    data: expiredSubscriptionsData,
    isLoading: expiredSubscriptionsLoading,
    error: expiredSubscriptionsError,
  } = useGetExpiredCooperativesSubscriptionsQuery(undefined, {
    skip: filterStatus !== "expired",
  });

  // Get the appropriate data based on filter
  const getCurrentData = () => {
    switch (filterStatus) {
      case "active":
        return {
          data: activeSubscriptionsData?.data || [],
          loading: activeSubscriptionsLoading,
          error: activeSubscriptionsError,
        };
      case "expiring":
        return {
          data: expiringSubscriptionsData?.data || [],
          loading: expiringSubscriptionsLoading,
          error: expiringSubscriptionsError,
        };
      case "expired":
        return {
          data: expiredSubscriptionsData?.data || [],
          loading: expiredSubscriptionsLoading,
          error: expiredSubscriptionsError,
        };
      default:
        return {
          data: allCooperativesData?.data || [],
          loading: allCooperativesLoading,
          error: allCooperativesError,
        };
    }
  };

  const { data: currentData, loading: currentLoading } = getCurrentData();

  // Filter cooperatives based on search query
  const filteredCooperatives = Array.isArray(currentData)
    ? currentData.filter((cooperative) => {
        if (!searchQuery.trim()) return true;

        const query = searchQuery.toLowerCase();
        return (
          cooperative.name?.toLowerCase().includes(query) ||
          cooperative.email?.toLowerCase().includes(query) ||
          cooperative.address?.toLowerCase().includes(query) ||
          cooperative.city?.toLowerCase().includes(query) ||
          cooperative.country?.toLowerCase().includes(query)
        );
      })
    : [];

  const cooperatives = filteredCooperatives.slice(0, 10);

  const handleUpdateSubscription = (cooperative) => {
    console.log("Update subscription clicked for:", cooperative);
    if (isUpdatingSubscription) return; // Prevent multiple clicks

    try {
      setIsUpdatingSubscription(true);
      setSelectedCooperative(cooperative);
      setIsUpdateModalOpen(true);

      // Reset loading state after a short delay
      setTimeout(() => {
        setIsUpdatingSubscription(false);
      }, 1000);
    } catch (error) {
      console.error("Error opening update modal:", error);
      setIsUpdatingSubscription(false);
    }
  };

  const handleCloseUpdateModal = () => {
    setIsUpdateModalOpen(false);
    setSelectedCooperative(null);
  };

  const handleUpdateSuccess = () => {
    // Refetch cooperatives data
    window.location.reload();
  };

  const renderCooperatives = (cooperatives) => {
    return cooperatives.map((sub, index) => (
      <CardComp
        key={sub.company_id || index}
        cardClassName="border-0 shadow p-4 rounded-xl bg-white mb-4 hover:shadow-lg transition-shadow"
      >
        <div className="flex justify-between items-center">
          <div
            className="flex items-center cursor-pointer flex-grow"
            onClick={() => {
              console.log("Navigating to cooperative ID:", sub.company_id);
              navigate(`/cooperatives/${sub.company_id}`);
            }}
          >
            <img
              src={sub.logo_url || "/Images/Dashboard/image1.svg"}
              alt="avatar"
              className="w-10 h-10 rounded-full mr-3"
            />
            <div>
              <p className="font-semibold text-lg text-gray-800">
                {sub.name || "-----"}
              </p>
              <p className="text-sm text-gray-500">{sub.address || "-----"}</p>
            </div>
          </div>
          <div className="flex items-center space-x-8">
            <div>
              <p className="text-xl font-semibold text-gray-800">
                {sub.total_branches !== undefined && sub.total_branches !== null
                  ? sub.total_branches
                  : "-----"}
              </p>
              <p className="text-sm text-gray-500">Branches</p>
            </div>
            <div>
              <p className="text-xl font-semibold text-gray-800">
                {sub.total_members !== undefined && sub.total_members !== null
                  ? sub.total_members
                  : "-----"}
              </p>
              <p className="text-sm text-gray-500">Total Member</p>
            </div>
            <div>
              <p className="text-[#19bc31] text-xl font-semibold">
                {sub.total_contributions_formatted ||
                  (sub.total_contributions !== undefined &&
                  sub.total_contributions !== null
                    ? formatMoney(sub.total_contributions)
                    : "-----")}
              </p>
              <p className="text-sm text-gray-500">Total Contributions</p>
            </div>
            <div>
              <p className="text-[#ff4343] text-xl font-semibold">
                {sub.total_loans_formatted ||
                  (sub.total_loans !== undefined && sub.total_loans !== null
                    ? formatMoney(sub.total_loans)
                    : "-----")}
              </p>
              <p className="text-sm text-gray-500">Loan Disbursed</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleUpdateSubscription(sub);
                }}
                disabled={isUpdatingSubscription}
                className="bg-[#8B313A] text-white px-3 py-1.5 rounded-md hover:bg-[#6B252A] transition-colors text-sm font-medium flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingSubscription ? (
                  <>
                    <svg
                      className="animate-spin w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Loading...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Update
                  </>
                )}
              </button>
              <BsArrowRight
                className="w-5 h-5 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  console.log(
                    "Arrow clicked - Navigating to cooperative ID:",
                    sub.company_id
                  );
                  navigate(`/cooperatives/${sub.company_id}`);
                }}
              />
            </div>
          </div>
        </div>
      </CardComp>
    ));
  };

  return (
    <div className="min-h-screen p-6">
      <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100">
        <div className="d-flex justify-content-between align-items-center">
          <h1 className="text-2xl font-bold text-[#45464e] mb-0">
            Cooperatives
          </h1>
          <button
            onClick={() => navigate("/cooperatives/create")}
            className="flex items-center gap-2 bg-[#8B313A] hover:bg-[#6B252A] text-white px-4 py-2 rounded-lg font-medium transition-colors border-0"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Cooperative
          </button>
        </div>
      </div>

      {/* Overview Section */}
      <div className="mt-5 mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Cooperatives Onboarded Card */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">
                  Cooperatives Onboarded
                </p>
                <p className="text-3xl font-bold text-gray-900">
                  {dashboardData?.data?.totalCooperatives || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Branches Card */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">
                  Branches
                </p>
                <p className="text-3xl font-bold text-gray-900">
                  {dashboardData?.data?.totalBranches ||
                    dashboardData?.data?.total_branches ||
                    0}
                </p>
              </div>
              <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-pink-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Members Onboarded Card */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">
                  Members Onboarded
                </p>
                <p className="text-3xl font-bold text-gray-900">
                  {dashboardData?.data?.totalMember ||
                    dashboardData?.data?.totalMembers ||
                    dashboardData?.data?.total_members ||
                    0}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cooperatives Subscription Section */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800">Cooperatives</h2>
          <div className="flex items-center space-x-4">
            {/* Search Input */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search cooperatives..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A] focus:border-transparent w-64"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter Dropdown */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">
                Filter by status:
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A] focus:border-transparent"
              >
                <option value="all">All Cooperatives</option>
                <option value="active">Active Subscriptions</option>
                <option value="expiring">Expiring Soon</option>
                <option value="expired">Expired Subscriptions</option>
              </select>
            </div>
          </div>
        </div>

        {currentLoading ? (
          <CooperativesListSkeleton rows={5} />
        ) : cooperatives.length > 0 ? (
          renderCooperatives(cooperatives)
        ) : (
          <EmptyTable
            Message={
              searchQuery.trim()
                ? `No cooperatives found matching "${searchQuery}"`
                : `No ${
                    filterStatus === "all"
                      ? "cooperatives"
                      : filterStatus + " subscriptions"
                  } found`
            }
          />
        )}
      </div>

      {/* Update Subscription Modal */}
      <UpdateSubscriptionModal
        isOpen={isUpdateModalOpen}
        onClose={handleCloseUpdateModal}
        cooperative={selectedCooperative}
        onSuccess={handleUpdateSuccess}
      />
    </div>
  );
}
